import React, {useEffect, useRef, useCallback} from 'react';
import {View, StyleSheet, FlatList, Dimensions, Platform} from 'react-native';
import {NavigationProp} from '@react-navigation/native';
import type {LocationType} from 'src/types/Location';
import type {TimeSlotType} from 'src/types/Menu';
import BackSubheader from '../../elements/BackSubheader';
import Localized from 'src/constants/AppStrings';
import {capitalizeFirstWord, getPreviousRouteName} from 'src/Util';
import {withForwardedNavigationParams} from 'react-navigation-props-mapper';
import Styles from 'src/components/Styles';
import NavActions from 'src/actions/NavActions';
import {alertError} from '../../helpers/AlertHelper';
import AVText from 'src/components/elements/AVText';
import TransactionActions from 'src/actions/TransactionActions';
import AppRoutes from 'src/AppRoutes';
import MenuActions from 'src/actions/MenuActions';
import uuid from 'src/nativeModules/UUID';
import Events from 'src/logging/Events';
import {generateErrorMessage} from 'src/logging/generateErrorMessage';
import CrashlyticsEvents from 'src/logging/Crashlytics';
import {getDescriber} from 'src/components/screens/orderAhead/descriptor/DescriptorType';
import RoundedAppButton, {
  RoundedAppButtonType,
} from 'src/components/elements/RoundedAppButton';
import {useAppSelector} from 'src/redux/hooks';
import PickupMobileOrderLocationList from 'src/components/elements/orderAhead/PickupMobileOrderLocationList';
import MainConsumerContext from 'src/components/MainConsumerContext';
import MenuService from 'src/services/MenuService';
import NoMobileOrderLocation from 'src/components/img/svg/NoMobileOrderLocation';
import {referralTemplate} from 'src/components/screens/referral/referralHelper';
import {useNearbyLocations, useSelectedLocation, useOrderAhead} from 'src/Util';
import {getLayoutDescriber} from 'src/components/elements/layoutDescriptor/DescriptorType';

type NewMobileOrderLocationScreenProps = {
  navigation: NavigationProp<NewMobileOrderLocationScreenProps>;
  location: LocationType;
  availableTimes: Array<TimeSlotType>;
  autoAdvanced: boolean;
};

const {getPickupLocationStyles, getInactiveButtonColor, getActiveButtonColor} =
  getDescriber();

const NewMobileOrderLocationScreen: React.FC<
  NewMobileOrderLocationScreenProps
> = (props) => {
  const diningLocations = useAppSelector(
    (s) => s.campusLocation.diningLocations,
  );

  const context = React.useContext(MainConsumerContext);
  const previousRoute = useRef<string | null>(null);

  // Custom hooks to separate logic
  const locationData = useNearbyLocations();
  const {
    selectedLocation: selectedMobileOrderLocation,
    selectLocation: selectedOrderLocation,
  } = useSelectedLocation(locationData);
  const {handleOrderAhead} = useOrderAhead();

  const locationDataLength =
    Array.isArray(locationData) && locationData.length > 0;

  useEffect(() => {
    const routeName = getPreviousRouteName(
      props.navigation?.getState()?.routes,
    );
    previousRoute.current = routeName;
  }, [props.navigation]);

  const getRouteFromLocationConfig = useCallback(
    async (location: LocationType, autoAdvanced = false) => {
      const {pickupLocations} = selectedMobileOrderLocation;

      let error = false;
      let selectedPickupLocation;
      let availableTimeSlots;

      if (pickupLocations?.length <= 1) {
        let pickupLocId = '';
        if (pickupLocations?.length === 1) {
          pickupLocId = pickupLocations[0].pickupLocationId;
          TransactionActions.pickupLocationUpdated(pickupLocations[0]);
          selectedPickupLocation = pickupLocations[0];
        }

        try {
          context?.actions.showSpinner();
          const availableTimes = await MenuActions.getAvailableTimeSlots(
            location,
            pickupLocId,
          );
          if (availableTimes.length > 0) {
            availableTimeSlots = MenuService.getAvailableTimeSlotsFromList(
              availableTimes,
              location.onlineOrderConfig.kitchenSchedule,
            );
          } else if (!autoAdvanced) {
            error = true;
            alertError(Localized.Errors.order_ahead_unavailable);
          } else {
            error = true;
            alertError(Localized.Errors.no_pickup_times_available);
          }
        } catch (err: any) {
          error = true;
          const guid = await uuid.getRandomUUID();
          CrashlyticsEvents.log(
            'Exception',
            'HomeScreen:GetRouteFromPickupLocations',
            generateErrorMessage(err),
            guid,
          );
          Events.Error.trackEvent(
            'Exception',
            'HomeScreen:GetRouteFromPickupLocations',
            generateErrorMessage(err),
            guid,
          );
          alertError(Localized.Errors.order_ahead_unavailable, guid);
        } finally {
          context?.actions.hideSpinner();
        }
      }

      return {
        availableTimes: availableTimeSlots,
        name: AppRoutes.NewPickupLocation,
        selectedPickupLocation,
        error,
      };
    },
    [context?.actions, selectedMobileOrderLocation],
  );

  const onBackSelect = () => {
    if (Platform.OS === 'web') {
      NavActions.navigate(AppRoutes.Home, {
        screen: AppRoutes.RewardsScreen,
      });
    } else {
      NavActions.navigate(AppRoutes.RewardsScreen);
    }
  };

  const onContinuePress = useCallback(async () => {
    if (locationDataLength) {
      TransactionActions.shoppingCartCleared();
      if (diningLocations.length > 0) {
        handleOrderAhead(diningLocations[0], false, getRouteFromLocationConfig);
      }
    } else {
      onBackSelect();
    }
  }, [
    locationDataLength,
    diningLocations,
    handleOrderAhead,
    getRouteFromLocationConfig,
  ]);

  const renderPickupLocations = useCallback(
    ({item, index}) => {
      const shouldRender = item.hasOwnProperty('hasOnlineOrdering')
        ? !!item.hasOnlineOrdering
        : true;
      if (!shouldRender) {
        return null;
      }
      return (
        <PickupMobileOrderLocationList
          item={item}
          index={index}
          selectMobileOrderLocation={selectedOrderLocation}
          selectedValue={
            item.locationId === selectedMobileOrderLocation?.locationId
          }
        />
      );
    },
    [selectedMobileOrderLocation, selectedOrderLocation],
  );

  const headerComponent = useCallback(
    () => (
      <View style={styles.headerView}>
        <AVText
          style={getPickupLocationStyles().headerText}
          accessible={true}
          testID="headerTitleText"
          accessibilityRole={'text'}
          accessibilityLabel={Localized.Labels.select_mobile_order_location}
          aria-label={Localized.Labels.select_mobile_order_location}
          maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm5}
        >
          {capitalizeFirstWord(Localized.Labels.select_mobile_order_location)}
        </AVText>
      </View>
    ),
    [],
  );

  const buttonLabel = locationDataLength
    ? Localized.Buttons.continue_to_pickup_time_location
    : Localized.Buttons.continue_to_rewards;

  return (
    <BackSubheader
      title={Localized.Labels.mobile_order_location}
      previousRoute={previousRoute.current}
      accessibilityLabel={'Back arrow'}
      accessibilityHint={`Press to navigate back to the ${previousRoute.current} screen`}
      onBackSelect={onBackSelect}
      pop={false}
    >
      <View style={styles.container}>
        {locationDataLength ? (
          <FlatList
            data={locationData}
            renderItem={renderPickupLocations}
            keyExtractor={(item) => item.locationId?.toString()}
            ListHeaderComponent={headerComponent}
            ListFooterComponent={<View style={styles.footer} />}
          />
        ) : (
          <>
            {headerComponent()}
            <View style={styles.locationView}>
              <NoMobileOrderLocation />
              <AVText style={styles.noLocationText}>
                {Localized.Buttons.mobile_order_error_msg}
              </AVText>
              <AVText style={styles.noLocationTextDesc}>
                {Localized.Buttons.mobile_order_error_msg_desc}
              </AVText>
            </View>
          </>
        )}
      </View>
      <View style={getPickupLocationStyles().buttonContainerStyle}>
        <RoundedAppButton
          buttonType={RoundedAppButtonType.Solid}
          buttonViewStyle={[
            getPickupLocationStyles().buttonView,
            {
              backgroundColor:
                selectedMobileOrderLocation === null
                  ? getInactiveButtonColor()
                  : getActiveButtonColor(),
            },
          ]}
          buttonContainerStyle={getPickupLocationStyles().containerStyle}
          accessibilityLabelValue={buttonLabel}
          onPress={onContinuePress}
          titleText={buttonLabel}
          accessibilityHintValue={'button'}
          aria-label={buttonLabel}
          textStyle={[getPickupLocationStyles().buttonText]}
          isBtnInactive={selectedMobileOrderLocation === null}
          testID="qa_btn_GoBack"
        />
      </View>
    </BackSubheader>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: Styles.Spacing.m3,
  },
  button: {
    alignSelf: 'center',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: Styles.Spacing.m3,
    paddingVertical: Styles.Spacing.m2,
  },
  footer: {
    height: Dimensions.get('screen').width / 4,
  },
  headerView: {
    marginTop: Styles.Heights.h2 + Styles.Spacing.m1,
  },
  locationView: {
    top: 32,
    justifyContent: 'center',
    alignItems: 'center',
  },
  noLocationText: {
    marginTop: 10,
    textAlign: 'center',
    fontFamily: referralTemplate.textFontFamily,
    fontSize: getLayoutDescriber().bodyLargeFontSize(),
    fontWeight: '700',
    color: Styles.black,
  },
  noLocationTextDesc: {
    marginTop: 10,
    textAlign: 'center',
    fontFamily: referralTemplate.textFontFamily,
    fontSize: getLayoutDescriber().bodyLargeFontSize(),
    fontWeight: '400',
    color: Styles.black,
  },
});

export default withForwardedNavigationParams<NewMobileOrderLocationScreenProps>()(
  NewMobileOrderLocationScreen,
);
